import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import { ActiveFeatureStreamsService } from 'src/app/shared/common/current-features/active-feature-streams.service';
import { MenuService } from 'src/app/shared/common/layout/menu.service';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { Layer } from 'src/app/shared/map-data-services/layer/layer';

import { Path } from 'leaflet';
import { GeometryTypes } from 'src/app/shared/common/utility/geometry-utils';
import { StringUtils } from 'src/app/shared/common/utility/string-utils';
import { CachedFeatureService } from 'src/app/shared/map-data-services/feature/cached-feature.service';
import { FeaturePropertyService } from 'src/app/shared/map-data-services/feature/feature-property.service';
import { LayerStyleGenerator } from 'src/app/shared/map-data-services/layer/layer-style-generator';
import {
    LayerSymbologyType,
    LineStyle,
    SymbologyOverrideChoice,
    SymbolShapeDefinition
} from 'src/app/shared/map-data-services/styles/style-symbology';
import { StyleSymbologyStreamService } from 'src/app/shared/map-data-services/styles/style-symbology-stream.service';
import { UserSettingsStreamService } from 'src/app/shared/user/user-settings-stream.service';
import { MapMenuCode } from '../../map-menus/map-menu-list';
import { markerClusterGroupOptions } from './cluster-layer.service';
import { FeatureMapLayer } from './feature-map-layer';
import { FeatureMapLayerCacheService } from './feature-map-layer-cache.service';

export class SymbologyProperties {
    geometryType: GeometryTypes;
    layerId: string;
    colorKey: string;
    active: boolean;
    selected: boolean;
    inVisibleTask: boolean;
    updated: boolean;

    // Label properties
    label?: string;
    labelSelectedOption?: string;

    // Custom symbology properties
    symbolId?: string;
    symbolType?: LayerSymbologyType;
    symbolName?: string;
    symbolFillColor?: string;
    symbolOutlineColor?: string;
    symbolText?: string;
    symbolTextColor?: string;

    // line, polygon properties
    lineStyle?: LineStyle;
    lineColor?: string;
    fillColor?: string;
    fillOpacity?: number;
}

@Injectable({
    providedIn: 'root'
})
export class FeatureMapLayersService {
    constructor(
        private featureMapLayerCacheService: FeatureMapLayerCacheService,
        private featurePropertyService: FeaturePropertyService,
        private activeFeatureStreams: ActiveFeatureStreamsService,
        private menuService: MenuService,
        private cachedFeatureService: CachedFeatureService,
        private styleSymbologyStream: StyleSymbologyStreamService,
        private userSettingsStreamService: UserSettingsStreamService
    ) {}

    // Save the marker icons for the layer in case of line features with symbology enabled.
    public layerSymbologyMarkerIconsMap: Map<string, L.MarkerOptions> = new Map<string, L.MarkerOptions>();
    public clearSymbologyMarkerIconsCache: boolean = false;
    private pendingOperations: Map<string, number> = new Map<string, number>();

    createFeatureMapLayer(feature: Feature, miniMap = false): FeatureMapLayer {
        if (!feature) {
            return null;
        }
        // If feature is in data tab, then dont show completed or incompleted status
        feature.inVisibleTask = this.menuService.activeMenusStream.getValue().includes(MapMenuCode.LAYERS)
            ? false
            : feature.inVisibleTask;
        let symbologyProperties = this.getFeatureSymbologyProperties(feature);

        // For mini map, we do not show labels
        if (miniMap) {
            // use 'undefined' for empty state (since we allow 'null' for labels to cover YesNo fields)
            symbologyProperties.label = undefined;
        }

        return this.createFeatureMapLayerFromGeometry(feature.geometry, symbologyProperties);
    }

    private createFeatureMapLayerFromGeometry(
        geometry: any,
        symbologyProperties: SymbologyProperties
    ): FeatureMapLayer {
        if (!geometry) {
            return null;
        }

        let geoType = geometry.type;

        if (geoType !== 'GeometryCollection' && !geometry.coordinates) {
            return null;
        }
        let coordinates: any[] = geometry.coordinates;

        switch (geoType) {
            case 'Point':
                let point = this.createFeatureMapLayerFromLatLngs(
                    symbologyProperties,
                    'Point',
                    L.latLng(coordinates[1], coordinates[0])
                );
                return point;

            case 'MultiPoint':
            case 'Curve':
                // wmsCoordinates is an Array[items][latlong]
                let points = new L.FeatureGroup();
                coordinates.forEach((item: any) => {
                    let point2 = this.createFeatureMapLayerFromLatLngs(
                        symbologyProperties,
                        'Point',
                        L.latLng(item[1], item[0])
                    );
                    if (point2) {
                        points.addLayer(point2);
                    }
                });
                return points as FeatureMapLayer;

            case 'LineString':
            case 'LinearRing':
                // wmsCoordinates is an Array[vertices][latlong]
                let lineLatLngs = coordinates.map((ogcCoordinate: any) => L.latLng(ogcCoordinate[1], ogcCoordinate[0]));
                let linePath = this.createFeatureMapLayerFromLatLngs(symbologyProperties, 'Line', lineLatLngs);
                return linePath;

            case 'MultiLineString':
                // wmsCoordinates is an Array[items][vertices][latlong]
                let linePaths = new L.FeatureGroup();
                coordinates.forEach((item: any) => {
                    let lineLatLngs2 = item.map((vertex: any) => L.latLng(vertex[1], vertex[0]));
                    let linePath2 = this.createFeatureMapLayerFromLatLngs(symbologyProperties, 'Line', lineLatLngs2);
                    if (linePath2) {
                        linePaths.addLayer(linePath2);
                    }
                });
                return linePaths as FeatureMapLayer;

            case 'Polygon':
                // wmsCoordinates is an Array[part][vertices][latlong] - first part is outer bounds;
                // other parts are internal holes (NB: currently we show only outer bounds)
                let polygonLatLngs:
                    | L.LatLngExpression
                    | L.LatLngExpression[]
                    | L.LatLngExpression[][]
                    | L.LatLngExpression[][][] = [];
                coordinates.forEach((polyCoords: any) => {
                    let latLngs = polyCoords.map((ogcCoordinate: any) => L.latLng(ogcCoordinate[1], ogcCoordinate[0]));
                    (polygonLatLngs as L.LatLngExpression[]).push(latLngs);
                });

                if (polygonLatLngs.length === 1) {
                    polygonLatLngs = polygonLatLngs[0];
                }

                let polygon = this.createFeatureMapLayerFromLatLngs(symbologyProperties, 'Polygon', polygonLatLngs);
                return polygon;

            case 'MultiPolygon':
                // wmsCoordinates is an Array[items][part][vertices][latlong]
                let polygons = new L.FeatureGroup();
                coordinates.forEach((item: any) => {
                    let polygonLatLngs2 = item[0].map((vertex: any) => L.latLng(vertex[1], vertex[0]));
                    let polygon2 = this.createFeatureMapLayerFromLatLngs(
                        symbologyProperties,
                        'Polygon',
                        polygonLatLngs2
                    );
                    if (polygon2) {
                        polygons.addLayer(polygon2);
                    }
                });
                return polygons as FeatureMapLayer;

            case 'GeometryCollection':
                // geometry collection
                if (!geometry.geometries) {
                    return null;
                }
                let featureGroup = new L.FeatureGroup();
                geometry.geometries.forEach((geometry2: any) => {
                    featureGroup.addLayer(this.createFeatureMapLayerFromGeometry(geometry2, symbologyProperties));
                });
                return featureGroup as FeatureMapLayer;
        }
    }

    private createFeatureMapLayerFromLatLngs(
        symbologyProperties: SymbologyProperties,
        geoType: string,
        latLngs: L.LatLngExpression | L.LatLngExpression[] | L.LatLngExpression[][] | L.LatLngExpression[][][]
    ): FeatureMapLayer {
        switch (geoType) {
            case 'Point':
                return new L.Marker(
                    latLngs as L.LatLngExpression,
                    this.getMarkerOptions(symbologyProperties)
                ) as FeatureMapLayer;
            case 'Line':
            case 'LineString':
                return new L.Polyline(
                    latLngs as L.LatLngExpression[],
                    this.getPolylineStyleOptions(symbologyProperties)
                ) as FeatureMapLayer;
            case 'Polygon':
                return new L.Polygon(
                    latLngs as L.LatLngExpression[][],
                    this.getPolygonStyleOptions(symbologyProperties)
                ) as FeatureMapLayer;
            default:
                return null;
        }
    }

    updateFeatureMapLayer(feature: Feature, featureMapLayer: FeatureMapLayer, checkActiveFeature = true): void {
        featureMapLayer = featureMapLayer || this.featureMapLayerCacheService.getFeatureMapLayer(feature.id);
        if (featureMapLayer && (featureMapLayer instanceof Path || featureMapLayer instanceof L.FeatureGroup)) {
            // this will set style for all child lines and polygons
            featureMapLayer.setStyle(this.getFeatureStyle(feature));
        }
        if (featureMapLayer && featureMapLayer instanceof L.Marker) {
            const symbologyProperties = this.getFeatureSymbologyProperties(feature);
            let markerOptions = this.getMarkerOptions(symbologyProperties);
            featureMapLayer.setIcon(markerOptions.icon);
            featureMapLayer.setZIndexOffset(markerOptions.zIndexOffset);
        }
        if (featureMapLayer && featureMapLayer instanceof L.LayerGroup) {
            featureMapLayer.eachLayer((childMapLayer: FeatureMapLayer) => {
                // set style for each point child
                this.updateFeatureMapLayer(feature, childMapLayer, checkActiveFeature);
            });
        }
        const layerId = feature.jobId ? feature.jobId : feature.layerId;
        this.featureMapLayerCacheService.addOrUpdateFeatureMapLayer(feature, featureMapLayer, layerId);
        if (checkActiveFeature) {
            let activeFeature = this.activeFeatureStreams.activeFeature;
            if (activeFeature && activeFeature.id === feature.id) {
                // updating the feature cache
                this.cachedFeatureService.addOrUpdateFeature(feature);
                this.activeFeatureStreams.activeFeature = activeFeature;
            }
        }
    }

    mergeChildToParentFeatureMapLayer(
        existingFeatureMapLayer: FeatureMapLayer,
        childFeatureMapLayer: FeatureMapLayer
    ): L.FeatureGroup<any> {
        // merge a child map layer (always a point from a GeometryCollection) into its parent map layer (arises from vector tiling)
        let featureGroup: L.FeatureGroup = null;
        if (childFeatureMapLayer) {
            featureGroup = new L.FeatureGroup();
            featureGroup.addLayer(childFeatureMapLayer);
        }
        return this.mergeFeatureMapLayers(existingFeatureMapLayer, featureGroup);
    }

    public mergeFeatureMapLayers(
        existingFeatureMapLayer: FeatureMapLayer,
        featureMapLayer: FeatureMapLayer
    ): L.FeatureGroup {
        // merge two feature map layers for the same feature into one.
        // Arises from the same feature, split over multiple tiles, each tile generating a map layer.
        existingFeatureMapLayer = existingFeatureMapLayer ? existingFeatureMapLayer : null;

        // flatten to a single-level array of map layers
        let flattenedFeatureMapLayer = this.flattenMapLayers(featureMapLayer);
        if (!existingFeatureMapLayer) {
            existingFeatureMapLayer = new L.FeatureGroup() as FeatureMapLayer;
        } else if (!(existingFeatureMapLayer instanceof L.FeatureGroup)) {
            // discrepancy in structure - simply replace
            existingFeatureMapLayer = new L.FeatureGroup() as FeatureMapLayer;
        }
        // add new map layers to existing FeatureGroup
        flattenedFeatureMapLayer.forEach(mapLayer => {
            (existingFeatureMapLayer as L.FeatureGroup).addLayer(mapLayer);
        });
        return existingFeatureMapLayer as L.FeatureGroup;
    }

    private flattenMapLayers(mapLayer: L.Layer): L.Layer[] {
        // reduce multi-level mapLayers to a single array of layers
        if (!(mapLayer instanceof L.LayerGroup)) {
            return [mapLayer];
        }
        return (mapLayer as L.LayerGroup)
            .getLayers()
            .reduce(
                (prevMapLayers: L.Layer[], currentMapLayers: L.Layer) =>
                    prevMapLayers.concat(this.flattenMapLayers(currentMapLayers)),
                []
            );
    }

    public getFeatureStyle(feature: Feature): L.PathOptions {
        if (!feature || !feature.geometry) {
            return {};
        }
        let symbologyProperties = this.getFeatureSymbologyProperties(feature);
        return this.getFeatureStyleFromGeometry(symbologyProperties, feature.geometry);
    }

    private getFeatureStyleFromGeometry(symbologyProperties: SymbologyProperties, geometry: any): L.PathOptions {
        if (!geometry) {
            return {};
        }
        let geoType = geometry.type;
        switch (geoType) {
            case 'Point':
            case 'MultiPoint':
            case 'Curve':
            default:
                return {};

            case 'LineString':
            case 'LinearRing':
            case 'MultiLineString':
                return this.getPolylineStyleOptions(symbologyProperties);

            case 'Polygon':
            case 'MultiPolygon':
                return this.getPolygonStyleOptions(symbologyProperties);

            case 'GeometryCollection':
                // this will cover both lines and polygons and is ignored for points
                return this.getPolygonStyleOptions(symbologyProperties);
        }
    }

    private getMarkerOptions(symbologyProperties: SymbologyProperties): L.MarkerOptions {
        return this.createCustomSymbolMarker(['marker'], symbologyProperties);
    }

    public createCustomSymbolMarker(classes: string[], symbologyProperties: SymbologyProperties): L.MarkerOptions {
        if (!classes?.length || !symbologyProperties) return;

        let symbolDefinition = this.styleSymbologyStream.symbolDefinitionsStream
            .getValue()
            .find(symbol => symbol.uuid === symbologyProperties.symbolId);

        if (!symbolDefinition) {
            // The feature was created before the symbology update.
            symbolDefinition = this.setDefaultSymbolDefinition(symbologyProperties);

            symbologyProperties.symbolId = symbologyProperties.layerId;
            symbologyProperties.symbolFillColor = '#' + symbologyProperties.colorKey;
            symbologyProperties.symbolOutlineColor = '#' + symbologyProperties.colorKey;
        }

        // make stroke and fill transparent by default on initial render on leaflet
        let icon = symbolDefinition?.svgImage;
        icon = this.hideAndReplaceInitialSymbolColorsInSvg(icon);

        // Apply symbology properties to the custom symbol markers
        this.applySymbologyPropertiesToCustomSymbolMarkers(symbologyProperties);

        const fillColor = symbologyProperties.symbolFillColor;
        const outlineColor = symbologyProperties.symbolOutlineColor;
        const iconText = symbologyProperties.symbolText;
        const iconTextColor = symbologyProperties.symbolTextColor;

        // Use this for debugging markers easily - do not remove this
        const uniqueClassName =
            `custom-symbol-${symbologyProperties.layerId}-${symbologyProperties.symbolId}-${fillColor}-${outlineColor}`.replace(
                /#/g,
                ''
            );
        classes.push('custom-symbol-marker');
        classes.push(uniqueClassName);

        // Store overrides information in data attributes
        const dataAttributes = `data-layer-id="${symbologyProperties.layerId}" data-symbol-id="${symbologyProperties.symbolId}" data-fill-color="${fillColor}" data-outline-color="${outlineColor}" data-symbol-text="${iconText}" data-symbol-text-color="${iconTextColor}"`;

        let html = `<div class="marker-background"><div class="custom-marker-inner" ${dataAttributes} style="margin-left: -12px; margin-top: -12px;">${icon}</div></div>`;

        let label = this.featurePropertyService.propertyValueTransformer(
            symbologyProperties.label,
            symbologyProperties.layerId,
            symbologyProperties.labelSelectedOption
        );

        if (label) {
            const truncatedLabel = StringUtils.truncate(label);
            html += `<span class="marker-label">${truncatedLabel}</span>`;
        }

        if (symbologyProperties.inVisibleTask) {
            classes.push(symbologyProperties.updated ? 'updated' : 'in-task');
            html =
                '<div class="marker-task custom-marker-task" style="border-color: #' +
                symbologyProperties.colorKey +
                ';"></div>' +
                html;
        }
        if (symbologyProperties.active) {
            classes.push('active');
        } else if (symbologyProperties.selected) {
            classes.push('selected');
        }

        // Symbology Overrides have been applied. Refresh the markers on the map.
        this.refreshSymbologyMarkerIcons(symbologyProperties, markerClusterGroupOptions.chunkDelay);

        const divIcon = L.divIcon({
            className: classes.join(' '),
            html: html,
            iconSize: null
        });

        return {
            icon: divIcon,
            interactive: false,
            zIndexOffset: symbologyProperties.active ? 2003 : symbologyProperties.selected ? 1003 : 3
        };
    }

    private setDefaultSymbolDefinition(symbologyProperties: SymbologyProperties) {
        let symbol: SymbolShapeDefinition;
        const availableSymbols = this.styleSymbologyStream.symbolDefinitionsStream.getValue();

        if (symbologyProperties.geometryType === GeometryTypes.POINT) {
            symbol =
                availableSymbols.find(symbol => symbol.uuid === this.styleSymbologyStream.DEFAULT_SYMBOL_UUID) ||
                availableSymbols.filter(symbol => symbol.type === LayerSymbologyType.GEOMETRIC)[0];
        }

        return symbol;
    }

    private applySymbologyPropertiesToCustomSymbolMarkers(symbologyProperties?: SymbologyProperties) {
        const svgElements = document.querySelectorAll(`.custom-marker-inner svg`);

        if (!svgElements.length && this.layerSymbologyMarkerIconsMap.has(symbologyProperties.layerId)) {
            if (!symbologyProperties?.layerId) return;

            // Symbology has been updated. Clear the outdated layerSymbologyMarkerIconsMap entry. Markers will be styled on the next mapAction.
            const symbolMarker = this.createCustomSymbolMarker(['marker', 'line-symbol'], symbologyProperties);
            this.layerSymbologyMarkerIconsMap.set(symbologyProperties.layerId, symbolMarker);
            return;
        }

        svgElements.forEach(element => {
            const htmlElement = element as HTMLElement;

            // `.custom-marker-inner` has data attributes with fill / outline colors
            const customMarkerInner = htmlElement.parentElement as HTMLElement;
            const fillColor = customMarkerInner.getAttribute('data-fill-color');
            const outlineColor = customMarkerInner.getAttribute('data-outline-color');
            const symbolTextColor = customMarkerInner.getAttribute('data-symbol-text-color');
            const symbolText = customMarkerInner.getAttribute('data-symbol-text');

            const definitionIcons = htmlElement.querySelectorAll('.sd-icon');
            definitionIcons.forEach(iconElement => {
                if (iconElement.classList.contains('sd-fill')) {
                    iconElement.setAttribute('fill', fillColor);
                }
                if (iconElement.classList.contains('sd-stroke')) {
                    iconElement.setAttribute('stroke', outlineColor);
                }
                // revert all white colors from hideAndReplaceInitialSymbolColorsInSvg();
                if (iconElement.getAttribute('fill') === '#ffffff00') iconElement.setAttribute('fill', 'white');
                if (iconElement.getAttribute('stroke') === '#ffffff00') iconElement.setAttribute('stroke', 'white');
            });

            const textElement = htmlElement.querySelector('.symbology-customizable-icon-text');
            if (textElement) {
                textElement.textContent = symbolText;
                textElement.setAttribute('fill', symbolTextColor);
            }
        });
    }

    private setDashArray(symbologyProperties: SymbologyProperties): string {
        let dashArray = '0,0';
        if (!symbologyProperties.inVisibleTask) {
            dashArray = '0,0';
        } else if (!symbologyProperties.updated) {
            dashArray = '7,10';
        }
        if (symbologyProperties.lineStyle === LineStyle.DASHED) {
            dashArray = '5,5';
        } else if (symbologyProperties.lineStyle === LineStyle.DOTTED) {
            dashArray = '1,5';
        }

        return dashArray;
    }

    public getPolylineStyleOptions(symbologyProperties: SymbologyProperties): L.PathOptions {
        if (symbologyProperties.symbolName) {
            if (!this.layerSymbologyMarkerIconsMap.has(symbologyProperties.layerId)) {
                // Custom symbol enabled. Symbol markers will be placed on lines, equidistantly.
                const symbolMarker = this.createCustomSymbolMarker(['marker', 'line-symbol'], symbologyProperties);
                this.layerSymbologyMarkerIconsMap.set(symbologyProperties.layerId, symbolMarker);
            } else {
                this.refreshSymbologyMarkerIcons(symbologyProperties, 0);
            }
        }

        return {
            interactive: false, // feature click treated like ordinary map click
            color: symbologyProperties.lineColor || '#' + symbologyProperties.colorKey,
            opacity: 1.0,
            weight: symbologyProperties.active ? 5 : symbologyProperties.selected ? 3 : 1,
            dashArray: this.setDashArray(symbologyProperties)
        };
    }

    private setPolygonFillOpacity(symbologyProperties: SymbologyProperties): number {
        let fillOpacity = symbologyProperties.fillColor ? symbologyProperties.fillOpacity / 100 : 0.2;
        if (symbologyProperties.active) {
            fillOpacity *= 4;
        } else if (symbologyProperties.selected) {
            fillOpacity *= 2.5;
        }

        return fillOpacity;
    }

    public getPolygonStyleOptions(symbologyProperties: SymbologyProperties): L.PolylineOptions {
        return {
            interactive: false, // feature click treated like ordinary map click
            fillColor: symbologyProperties.fillColor || '#' + symbologyProperties.colorKey,
            color: symbologyProperties.lineColor || '#' + symbologyProperties.colorKey,
            opacity: 1,
            fillOpacity: this.setPolygonFillOpacity(symbologyProperties),
            weight: symbologyProperties.active ? 5 : symbologyProperties.selected ? 3 : 1,
            dashArray: this.setDashArray(symbologyProperties)
        };
    }

    private overrideFeatureSymbology(
        properties: SymbologyProperties,
        override: SymbologyOverrideChoice
    ): SymbologyProperties {
        properties.lineColor = LayerStyleGenerator.decimalToHexRgb(override.lineColor);
        properties.symbolFillColor = LayerStyleGenerator.decimalToHexRgb(override.fillColor);
        properties.fillColor = LayerStyleGenerator.decimalToHexRgb(override.fillColor);

        properties.symbolOutlineColor =
            properties.geometryType === GeometryTypes.POINT
                ? LayerStyleGenerator.decimalToHexRgb(override.outlineColor)
                : properties.lineColor;

        if (
            properties.geometryType === GeometryTypes.POINT &&
            properties.symbolType === LayerSymbologyType.CUSTOMIZABLE
        ) {
            properties.symbolTextColor = LayerStyleGenerator.decimalToHexRgb(override.color);
            properties.symbolOutlineColor = LayerStyleGenerator.decimalToHexRgb(override.fillColor);
        }

        return properties;
    }

    private getFeatureSymbologyProperties(feature: Feature): SymbologyProperties {
        if (!feature) return null;

        const layerIdLabelMapSettings =
            this.userSettingsStreamService.getCurrentMapWorkspaceSettings()?.layerIdLabelMap;

        const labelPropertyName = layerIdLabelMapSettings?.[feature.layerId] || undefined;

        const getFeatureLabel = (): string | undefined => {
            const propertyValue = feature.properties?.[labelPropertyName] || null;

            // allow 'true/false/null' values to be displayed in the label - set undefined for empty state
            return typeof propertyValue === 'boolean' || propertyValue === null || propertyValue
                ? propertyValue
                : undefined;
        };
        let properties: SymbologyProperties = {
            geometryType: feature.geometry.type,
            layerId: feature.layerId,
            colorKey: feature.colorKey,
            selected: Boolean(feature.selected),
            active: Boolean(feature.active),
            inVisibleTask: Boolean(feature.inVisibleTask),
            updated: Boolean(feature.updated),

            label: getFeatureLabel(),
            labelSelectedOption: labelPropertyName,

            symbolId: feature.style?.symbol?.symbolDefinitionUuid,
            symbolType: feature.style?.symbol?.type,
            symbolName: feature.style?.symbol?.symbolName,
            symbolFillColor: LayerStyleGenerator.decimalToHexRgb(feature.style?.symbol?.fillColor),
            symbolOutlineColor:
                feature.geometry.type === GeometryTypes.POINT
                    ? LayerStyleGenerator.decimalToHexRgb(feature.style?.symbol?.outlineColor)
                    : LayerStyleGenerator.decimalToHexRgb(feature.style?.lineColor),
            symbolText: feature.style?.symbol?.text,
            symbolTextColor: LayerStyleGenerator.decimalToHexRgb(feature.style?.symbol?.color),

            lineStyle: feature.style?.lineStyle,
            lineColor: LayerStyleGenerator.decimalToHexRgb(feature.style?.lineColor),
            fillColor: LayerStyleGenerator.decimalToHexRgb(feature.style?.fillColor),
            fillOpacity: feature.style?.fillOpacity
        };

        if (feature.style?.styleOverrides) {
            const symbologyOverrideChoices = feature.style.styleOverrides as SymbologyOverrideChoice[];

            const matchingOverride = symbologyOverrideChoices.find(
                choice => feature.properties?.[choice.choiceField] === choice.choiceValue
            );

            if (matchingOverride) {
                properties = this.overrideFeatureSymbology(properties, matchingOverride);
            }
        }

        if (
            feature.geometryType === GeometryTypes.POINT &&
            feature.style?.symbol.type === LayerSymbologyType.CUSTOMIZABLE
        ) {
            properties.symbolOutlineColor = properties.symbolTextColor;
            properties.symbolFillColor = '#ffffff';
        }
        // Used by layer-list to toggle sublayer visibility.
        this.cachedFeatureService.storeFeature(feature, properties);

        return properties;
    }

    public setLayerSymbologyProperties(feature: Feature, layerOrColorKey: Layer | string): void {
        let colorKey: string;
        if (layerOrColorKey instanceof Layer) {
            let layer = layerOrColorKey;
            colorKey = layer.getLayerColorKey();
        } else {
            colorKey = layerOrColorKey;
        }
        feature.colorKey = colorKey ? colorKey : '990000';
    }

    /**
     * Clear any pending operation for a specific symbologyProperty.layerId.
     * @param layerId The ID of the layer to clear operations for.
     */
    private clearPendingOperation(layerId: string): void {
        const timeoutId = this.pendingOperations.get(layerId);
        if (timeoutId !== undefined) {
            window.clearTimeout(timeoutId);
            this.pendingOperations.delete(layerId);
        }
    }

    // Bottleneck functionality here: This is called too frequently during map interactions - zoom in / out, pan, selecting features
    public refreshSymbologyMarkerIcons(
        symbologyProperties: SymbologyProperties,
        timeout = markerClusterGroupOptions.chunkDelay
    ): void {
        if (!symbologyProperties?.layerId) return;

        const layerId = symbologyProperties?.layerId;
        // Cancel any existing layer operations to clear the way for the new one - this is happening during map interactions
        this.clearPendingOperation(layerId);

        const timeoutId = window.setTimeout(() => {
            this.applySymbologyPropertiesToCustomSymbolMarkers(symbologyProperties);

            // Clean up the entry after execution
            this.pendingOperations.delete(layerId);
        }, timeout);

        this.pendingOperations.set(layerId, timeoutId);
    }

    private hideAndReplaceInitialSymbolColorsInSvg(icon: string): string {
        if (!icon) return '';

        // replace all "black" string value to transparent
        icon = icon.replace(/fill="black"/g, 'fill="transparent"').replace(/stroke="black"/g, 'stroke="transparent"');

        // replace all white with alpha 0 in hex format - so we can easily to revert back after refreshSymbologyMarkerIcons
        icon = icon.replace(/fill="white"/g, 'fill="#ffffff00"').replace(/stroke="white"/g, 'stroke="#ffffff00"');
        return icon;
    }

    public onCustomSymbologyFeature(
        feature: Feature,
        tileClusterLayerThis: any,
        markerInterval = 100, // Set markerInterval to 100 pixels
        skipCache = false
    ) {
        // Temporarily disable symbols over lines (TCMAPS-4525).
        return;
        if (this.clearSymbologyMarkerIconsCache) {
            this.layerSymbologyMarkerIconsMap = new Map<string, L.MarkerOptions>();
            this.clearSymbologyMarkerIconsCache = false;
        }

        const lineDistance = (point1: L.Point, point2: L.Point): number => {
            // Calculate Euclidean distance in pixels
            const deltaX = point2.x - point1.x;
            const deltaY = point2.y - point1.y;
            return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        };

        const addSvgMarkers = (coordinates: L.LatLng[], retryCount: number = 0) => {
            const MAX_RETRIES = 5;

            let markerOptions: L.MarkerOptions = this.layerSymbologyMarkerIconsMap.get(feature.layerId);

            if (!markerOptions) {
                if (retryCount < MAX_RETRIES) {
                    // Retry on the next event loop.
                    setTimeout(() => addSvgMarkers(coordinates, retryCount + 1), 0);
                }
                return;
            }

            const markerLayerGroup = L.layerGroup();
            const createMarker = (latlng: L.LatLng) => L.marker(latlng, markerOptions);

            coordinates.forEach((coord: L.LatLng, index: number) => {
                const coordPoint = tileClusterLayerThis.map.latLngToLayerPoint(coord);

                // Always place marker at vertices/end points.
                markerLayerGroup.addLayer(createMarker(coord));

                if (index > 0) {
                    const previousCoord = coordinates[index - 1];
                    const previousCoordPoint = tileClusterLayerThis.map.latLngToLayerPoint(previousCoord);
                    const segmentDistance = lineDistance(previousCoordPoint, coordPoint);

                    const numMarkers = Math.floor(segmentDistance / (2 * markerInterval));

                    for (let j = 1; j <= numMarkers; j++) {
                        const ratio = j / (numMarkers + 1);
                        const midPoint = L.latLng(
                            previousCoord.lat + (coord.lat - previousCoord.lat) * ratio,
                            previousCoord.lng + (coord.lng - previousCoord.lng) * ratio
                        );

                        markerLayerGroup.addLayer(createMarker(midPoint));
                    }
                }
            });

            markerLayerGroup.addTo(tileClusterLayerThis.map);

            if (feature.id) {
                tileClusterLayerThis.symbologyMarkerLayerGroupCache.set(feature.id, markerLayerGroup);
            }
        };

        if (!tileClusterLayerThis.symbologyMarkerLayerGroupCache) {
            tileClusterLayerThis.symbologyMarkerLayerGroupCache = new Map<string, L.LayerGroup>();
        }

        if (!tileClusterLayerThis.symbologyMarkerLayerGroupCache.has(feature.id) || skipCache) {
            if (feature.geometry.type === GeometryTypes.GEOMETRY_COLLECTION) {
                (feature.geometry as any).geometries.forEach((geometry: any) => {
                    if (geometry.type === GeometryTypes.LINE_STRING && feature.style.symbol) {
                        const coordinates: L.LatLng[] = geometry.coordinates.map((coord: [number, number]) =>
                            L.latLng(coord[1], coord[0])
                        );
                        addSvgMarkers(coordinates);
                    }
                });
            }
        } else {
            tileClusterLayerThis.symbologyMarkerLayerGroupCache.get(feature.id).addTo(tileClusterLayerThis.map);
        }
    }
}
